import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { getCategoriesWithProducts, searchProducts } from "../services/productService";
import { translateText } from "../services/translationService";

export default function Navbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [hoveredCategory, setHoveredCategory] = useState(null);
  const [hoveredSubCategory, setHoveredSubCategory] = useState(null);
  const [categories, setCategories] = useState([]);
  const [translatedCategories, setTranslatedCategories] = useState([]);
  const [language, setLanguage] = useState("fr");
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);
  
  const auth = useAuth();
  const { user, logout } = auth || {};
  const { cart } = useCart();
  const location = useLocation();
  const navigate = useNavigate();

  const cartItemsCount = cart?.items?.reduce((total, item) => total + (item.quantity || 0), 0) || 0;

  // Dictionnaire de traduction pour les textes statiques
  const staticTranslations = {
    fr: {
      search: "Rechercher...",
      help: "Aide & Contact",
      allProducts: "TOUS LES PRODUITS",
      womensCare: "SOINS FEMME",
      mensCare: "SOINS HOMME",
      unisexCare: "SOINS UNISEXE",
      loyaltyProgram: "Programme Fidélité",
      theMag: "Le Mag",
      ourUniverse: "Notre Univers",
      login: "Connexion",
      myAccount: "Mon Compte",
      logout: "Déconnexion",
      cart: "Panier",
      viewAll: "Voir tout →",
      allProductsTitle: "Tous nos produits de soin",
      womensCollection: "Collection Femme",
      mensCollection: "Collection Homme",
      unisexCollection: "Collection Unisexe",
      allWomenProducts: "Tous les produits femme →",
      allMenProducts: "Tous les produits homme →",
      allUnisexProducts: "Tous les produits unisexe →",
      ourBestProducts: "Nos meilleurs produits",
      products: "produits",
      searchResults: "Résultats de recherche",
      noResults: "Aucun résultat trouvé",
      loading: "Chargement...",
      language: "Langue",
      translating: "Traduction en cours...",
      menu: "Menu",
      close: "Fermer",
      categories: "Catégories",
      account: "Compte"
    },
    en: {
      search: "Search...",
      help: "Help & Contact",
      allProducts: "ALL PRODUCTS",
      womensCare: "WOMEN'S CARE",
      mensCare: "MEN'S CARE",
      unisexCare: "UNISEX CARE",
      loyaltyProgram: "Loyalty Program",
      theMag: "The Mag",
      ourUniverse: "Our Universe",
      login: "Login",
      myAccount: "My Account",
      logout: "Logout",
      cart: "Cart",
      viewAll: "View all →",
      allProductsTitle: "All Our Care Products",
      womensCollection: "Women's Collection",
      mensCollection: "Men's Collection",
      unisexCollection: "Unisex Collection",
      allWomenProducts: "All Women Products →",
      allMenProducts: "All Men Products →",
      allUnisexProducts: "All Unisex Products →",
      ourBestProducts: "Our Best Products",
      products: "products",
      searchResults: "Search Results",
      noResults: "No results found",
      loading: "Loading...",
      language: "Language",
      translating: "Translating...",
      menu: "Menu",
      close: "Close",
      categories: "Categories",
      account: "Account"
    },
    ar: {
      search: "بحث...",
      help: "المساعدة والاتصال",
      allProducts: "جميع المنتجات",
      womensCare: "العناية بالمرأة",
      mensCare: "العناية بالرجل",
      unisexCare: "العناية للجنسين",
      loyaltyProgram: "برنامج الولاء",
      theMag: "المجلة",
      ourUniverse: "عالمنا",
      login: "تسجيل الدخول",
      myAccount: "حسابي",
      logout: "تسجيل الخروج",
      cart: "عربة التسوق",
      viewAll: "عرض الكل →",
      allProductsTitle: "جميع منتجات العناية لدينا",
      womensCollection: "مجموعة المرأة",
      mensCollection: "مجموعة الرجل",
      unisexCollection: "مجموعة للجنسين",
      allWomenProducts: "جميع منتجات المرأة →",
      allMenProducts: "جميع منتجات الرجل →",
      allUnisexProducts: "جميع المنتجات للجنسين →",
      ourBestProducts: "أفضل منتجاتنا",
      products: "منتجات",
      searchResults: "نتائج البحث",
      noResults: "لم يتم العثور على نتائج",
      loading: "جاري التحميل...",
      language: "اللغة",
      translating: "جاري الترجمة...",
      menu: "القائمة",
      close: "إغلاق",
      categories: "الفئات",
      account: "الحساب"
    }
  };

  const t = (key) => staticTranslations[language]?.[key] || key;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await getCategoriesWithProducts();
        setCategories(data || []);
        setTranslatedCategories(data || []);
      } catch (err) {
        console.error("Error loading categories:", err);
        setCategories([]);
        setTranslatedCategories([]);
      }
    };
    fetchData();
  }, []);

  // Traduction des catégories et produits quand la langue change
  useEffect(() => {
    const translateCategories = async () => {
      if (categories.length === 0 || language === 'fr') {
        setTranslatedCategories(categories);
        return;
      }

      setIsTranslating(true);
      try {
        console.log(`🔄 Traduction des ${categories.length} catégories vers ${language}...`);
        
        const translated = await Promise.all(
          categories.map(async (category) => {
            // Traduire le nom de la catégorie
            const translatedName = await translateText(category.name, language);
            
            // Traduire les produits de la catégorie
            const translatedProducts = await Promise.all(
              (category.products || []).map(async (product) => ({
                ...product,
                name: await translateText(product.name, language),
                description: await translateText(product.description || '', language)
              }))
            );

            return {
              ...category,
              name: translatedName,
              products: translatedProducts
            };
          })
        );

        setTranslatedCategories(translated);
        console.log(`✅ Traduction terminée pour ${translated.length} catégories`);
        
      } catch (error) {
        console.error('❌ Erreur de traduction:', error);
        setTranslatedCategories(categories);
      } finally {
        setIsTranslating(false);
      }
    };

    translateCategories();
  }, [categories, language]);

  // Recherche en temps réel
  useEffect(() => {
    const performSearch = async () => {
      if (searchQuery.trim().length < 2) {
        setSearchResults([]);
        return;
      }

      setIsSearching(true);
      try {
        let results = await searchProducts(searchQuery);
        
        // Traduire les résultats de recherche si nécessaire
        if (language !== 'fr' && results.length > 0) {
          console.log(`🌐 Traduction des ${results.length} résultats de recherche...`);
          results = await Promise.all(
            results.map(async (product) => ({
              ...product,
              name: await translateText(product.name, language),
              description: await translateText(product.description || '', language)
            }))
          );
        }
        
        setSearchResults(results || []);
      } catch (error) {
        console.error("Search error:", error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    };

    const timeoutId = setTimeout(performSearch, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery, language]);

  const handleLogout = () => {
    if (typeof logout === 'function') {
      logout();
      navigate("/");
    } else {
      localStorage.removeItem('user');
      localStorage.removeItem('userCart');
      window.location.href = '/';
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/shop?search=${encodeURIComponent(searchQuery)}`);
      setIsSearchOpen(false);
      setSearchQuery("");
    }
  };

  const handleSearchResultClick = (productId) => {
    navigate(`/product/${productId}`);
    setIsSearchOpen(false);
    setSearchQuery("");
  };

  const isActiveRoute = (path) => location.pathname === path;

  // Organize categories by gender using translated categories
  const menCategories = translatedCategories.filter(cat => cat.gender === 'men');
  const womenCategories = translatedCategories.filter(cat => cat.gender === 'women');
  const unisexCategories = translatedCategories.filter(cat => !cat.gender || cat.gender === 'unisex');

  // Get all products from all categories
  const allProducts = translatedCategories.flatMap(cat => cat.products || []).slice(0, 8);

  // Function to get product image with correct path
  const getProductImage = (product) => {
    if (!product) return '/images/placeholder.jpg';
    
    if (product.images && typeof product.images === 'string') {
      try {
        const cleanJsonString = product.images.replace(/\\"/g, '"');
        const imagesArray = JSON.parse(cleanJsonString);
        if (imagesArray.length > 0 && imagesArray[0]) {
          return `/images/products/${imagesArray[0]}`;
        }
      } catch (error) {
        console.error('Error parsing JSON:', error);
      }
    }
    
    if (product.image) {
      return `/images/products/${product.image}`;
    }
    
    return '/images/placeholder.jpg';
  };

  // Function to display products horizontally
  const renderProductsPreview = (category, isAllProducts = false) => {
    const products = isAllProducts ? allProducts : (category?.products || []);
    if (products.length === 0) return null;

    return (
      <div className="absolute left-0 top-full mt-2 bg-white shadow-xl rounded-lg p-4 w-full max-w-4xl z-50 border border-[#d0b125]/20 hidden lg:block">
        <div className="flex justify-between items-center mb-4">
          <h4 className="font-medium text-[#2f2a1e] text-lg">
            {isAllProducts ? t('ourBestProducts') : `${category.name}`}
          </h4>
          <Link
            to={isAllProducts ? "/shop" : `/shop/${category.id}`}
            className="text-sm text-[#d0b125] hover:text-[#2f2a1e] transition-colors font-medium"
            onClick={() => {
              setHoveredCategory(null);
              setHoveredSubCategory(null);
            }}
          >
            {t('viewAll')}
          </Link>
        </div>
        <div className="flex space-x-4 overflow-x-auto pb-2">
          {products.map((product) => (
            <Link
              key={product.id}
              to={`/product/${product.id}`}
              className="flex-shrink-0 w-32 group/product"
              onClick={() => {
                setHoveredCategory(null);
                setHoveredSubCategory(null);
              }}
            >
              <div className="bg-gray-50 rounded-lg p-3 mb-2 group-hover/product:bg-[#fffaf3] transition-colors h-24 flex items-center justify-center">
                <img 
                  src={getProductImage(product)}
                  alt={product.name}
                  className="w-16 h-16 object-cover rounded mx-auto"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    const fallback = e.target.nextSibling;
                    if (fallback) fallback.style.display = 'flex';
                  }}
                />
                <div 
                  className="w-16 h-16 bg-gray-200 rounded items-center justify-center mx-auto hidden"
                >
                  <span className="text-gray-400 text-xl">📦</span>
                </div>
              </div>
              <div className="text-center">
                <p className="text-sm text-[#2f2a1e] font-medium truncate group-hover/product:text-[#d0b125] transition-colors">
                  {product.name}
                </p>
                <p className="text-xs text-gray-500 mt-1">{product.price} MAD</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    );
  };

  // Language options
  const languageOptions = [
    { code: "fr", name: "Français", flag: "🇫🇷" },
    { code: "en", name: "English", flag: "🇺🇸" },
    { code: "ar", name: "العربية", flag: "🇸🇦" }
  ];

  const handleLanguageChange = async (langCode) => {
    setLanguage(langCode);
    setIsLanguageOpen(false);
    localStorage.setItem('preferredLanguage', langCode);
  };

  // Charger la langue sauvegardée au démarrage
  useEffect(() => {
    const savedLanguage = localStorage.getItem('preferredLanguage');
    if (savedLanguage && ['fr', 'en', 'ar'].includes(savedLanguage)) {
      setLanguage(savedLanguage);
    }
  }, []);

  const getCurrentLanguage = () => {
    return languageOptions.find(lang => lang.code === language) || languageOptions[0];
  };

  // Rendu des résultats de recherche
  const renderSearchResults = () => {
    if (!isSearchOpen || !searchQuery) return null;

    return (
      <div className="absolute top-full left-0 right-0 mt-2 bg-white shadow-xl rounded-lg max-h-96 overflow-y-auto z-50 border border-[#d0b125]/20">
        <div className="p-4">
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-medium text-[#2f2a1e]">{t('searchResults')}</h3>
            <span className="text-sm text-gray-500">
              {searchResults.length} {t('products')}
            </span>
          </div>

          {isSearching ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#d0b125]"></div>
              <span className="ml-2 text-gray-500">{t('loading')}</span>
            </div>
          ) : searchResults.length > 0 ? (
            <div className="space-y-3">
              {searchResults.map((product) => (
                <div
                  key={product.id}
                  className="flex items-center p-3 hover:bg-[#fffaf3] rounded-lg cursor-pointer transition-colors group"
                  onClick={() => handleSearchResultClick(product.id)}
                >
                  <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                    <img
                      src={getProductImage(product)}
                      alt={product.name}
                      className="w-10 h-10 object-cover rounded"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className="w-10 h-10 bg-gray-200 rounded items-center justify-center hidden">
                      <span className="text-gray-400">📦</span>
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-[#2f2a1e] truncate group-hover:text-[#d0b125]">
                      {product.name}
                    </p>
                    <p className="text-sm text-gray-500 truncate">
                      {product.description}
                    </p>
                  </div>
                  <div className="text-[#d0b125] font-semibold ml-2">
                    {product.price} MAD
                  </div>
                </div>
              ))}
            </div>
          ) : searchQuery.length >= 2 ? (
            <div className="text-center py-8 text-gray-500">
              {t('noResults')} "{searchQuery}"
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  // Mobile menu content
  const renderMobileMenu = () => (
    <div className="fixed inset-0 z-50 bg-white lg:hidden">
      {/* Header mobile */}
      <div className="bg-[#fffaf3] border-b border-[#d0b125]/30 p-4">
        <div className="flex items-center justify-between">
          <Link 
            to="/" 
            className="flex-shrink-0"
            onClick={() => setIsMenuOpen(false)}
          >
            <img
              src="/images/logi d4u dré noir.png"
              alt="D4U Naturally"
              className="h-12 w-auto object-contain"
              onError={(e) => {
                e.target.src = '/images/placeholder.jpg';
              }}
            />
          </Link>
          
          <div className="flex items-center space-x-4">
            {/* Language Selector Mobile */}
            <div className="relative">
              <button
                onClick={() => setIsLanguageOpen(!isLanguageOpen)}
                className="flex items-center space-x-1 text-[#2f2a1e]"
              >
                <span>{getCurrentLanguage().flag}</span>
                <span className="text-sm uppercase">{getCurrentLanguage().code}</span>
              </button>
              
              {isLanguageOpen && (
                <div className="absolute top-full right-0 mt-2 bg-white shadow-lg rounded-lg py-2 w-32 z-50 border border-[#d0b125]/20">
                  {languageOptions.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => handleLanguageChange(lang.code)}
                      className={`flex items-center space-x-2 w-full px-3 py-2 text-left hover:bg-[#fffaf3] transition-colors ${
                        language === lang.code ? "text-[#d0b125] bg-[#fffaf3]" : "text-[#2f2a1e]"
                      }`}
                    >
                      <span>{lang.flag}</span>
                      <span className="flex-1 text-sm">{lang.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button
              onClick={() => setIsMenuOpen(false)}
              className="text-2xl text-[#2f2a1e] hover:text-[#d0b125] transition-colors"
            >
              ✕
            </button>
          </div>
        </div>
      </div>

      {/* Search Bar Mobile */}
      <div className="p-4 border-b border-gray-200">
        <form onSubmit={handleSearchSubmit} className="relative">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('search')}
            className="w-full px-4 py-3 pl-10 pr-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#d0b125] focus:border-[#d0b125]"
          />
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
            🔍
          </div>
          <button
            type="submit"
            className="absolute right-3 top-1/2 transform -translate-y-1/2 bg-[#d0b125] text-white px-3 py-1 rounded text-sm hover:bg-[#b89a20] transition-colors"
          >
            {t('search')}
          </button>
        </form>
      </div>

      {/* Navigation Mobile */}
      <div className="overflow-y-auto h-full pb-20">
        {/* User Section */}
        <div className="p-4 border-b border-gray-200 bg-gray-50">
          {user ? (
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-[#2f2a1e]">Bonjour, {user.name}</p>
                <p className="text-sm text-gray-600">{user.email}</p>
              </div>
              <div className="flex space-x-2">
                <Link
                  to="/my-account"
                  className="px-3 py-1 bg-[#d0b125] text-white rounded text-sm hover:bg-[#b89a20] transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {t('myAccount')}
                </Link>
                <button
                  onClick={() => {
                    handleLogout();
                    setIsMenuOpen(false);
                  }}
                  className="px-3 py-1 border border-red-500 text-red-500 rounded text-sm hover:bg-red-50 transition-colors"
                >
                  {t('logout')}
                </button>
              </div>
            </div>
          ) : (
            <div className="flex space-x-2">
              <Link
                to="/login"
                className="flex-1 text-center py-2 bg-[#d0b125] text-white rounded hover:bg-[#b89a20] transition-colors font-medium"
                onClick={() => setIsMenuOpen(false)}
              >
                {t('login')}
              </Link>
              <Link
                to="/register"
                className="flex-1 text-center py-2 border border-[#d0b125] text-[#d0b125] rounded hover:bg-[#fffaf3] transition-colors font-medium"
                onClick={() => setIsMenuOpen(false)}
              >
                S'inscrire
              </Link>
            </div>
          )}
        </div>

        {/* Main Categories */}
        <div className="p-4">
          <h3 className="font-serif text-lg text-[#2f2a1e] mb-4 font-medium">{t('categories')}</h3>
          
          {/* All Products */}
          <Link
            to="/shop"
            className="flex items-center justify-between py-3 px-4 bg-[#fffaf3] rounded-lg mb-2 hover:bg-[#d0b125] hover:text-white transition-colors group"
            onClick={() => setIsMenuOpen(false)}
          >
            <span className="font-medium">{t('allProducts')}</span>
            <span className="text-sm text-gray-500 group-hover:text-white">
              {allProducts.length}+
            </span>
          </Link>

          {/* Women Categories */}
          {womenCategories.length > 0 && (
            <div className="mb-4">
              <Link 
                to="/shop?gender=women"
                className="flex items-center justify-between py-3 px-4 bg-white border border-[#d0b125]/20 rounded-lg mb-2 hover:bg-[#fffaf3] transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                <span className="font-medium text-[#2f2a1e]">{t('womensCare')}</span>
                <span className="text-sm text-gray-500">→</span>
              </Link>
              <div className="ml-4 space-y-1">
                {womenCategories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/shop/${cat.id}`}
                    className="flex items-center justify-between py-2 px-3 text-sm text-[#2f2a1e] hover:text-[#d0b125] transition-colors rounded hover:bg-gray-50"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <span>{cat.name}</span>
                    <span className="text-xs text-gray-400">({cat.products?.length || 0})</span>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Men Categories */}
          {menCategories.length > 0 && (
            <div className="mb-4">
              <Link 
                to="/shop?gender=men"
                className="flex items-center justify-between py-3 px-4 bg-white border border-[#2f2a1e]/20 rounded-lg mb-2 hover:bg-gray-50 transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                <span className="font-medium text-[#2f2a1e]">{t('mensCare')}</span>
                <span className="text-sm text-gray-500">→</span>
              </Link>
              <div className="ml-4 space-y-1">
                {menCategories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/shop/${cat.id}`}
                    className="flex items-center justify-between py-2 px-3 text-sm text-[#2f2a1e] hover:text-[#d0b125] transition-colors rounded hover:bg-gray-50"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <span>{cat.name}</span>
                    <span className="text-xs text-gray-400">({cat.products?.length || 0})</span>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Unisex Categories */}
          {unisexCategories.length > 0 && (
            <div className="mb-4">
              <Link 
                to="/shop?gender=unisex"
                className="flex items-center justify-between py-3 px-4 bg-white border border-gray-300 rounded-lg mb-2 hover:bg-gray-50 transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                <span className="font-medium text-[#2f2a1e]">{t('unisexCare')}</span>
                <span className="text-sm text-gray-500">→</span>
              </Link>
              <div className="ml-4 space-y-1">
                {unisexCategories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/shop/${cat.id}`}
                    className="flex items-center justify-between py-2 px-3 text-sm text-[#2f2a1e] hover:text-[#d0b125] transition-colors rounded hover:bg-gray-50"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <span>{cat.name}</span>
                    <span className="text-xs text-gray-400">({cat.products?.length || 0})</span>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Additional Links */}
        <div className="p-4 border-t border-gray-200">
          <h3 className="font-serif text-lg text-[#2f2a1e] mb-4 font-medium">{t('ourUniverse')}</h3>
          <div className="space-y-2">
            <Link
              to="/loyalty-program"
              className="flex items-center py-2 px-3 text-[#2f2a1e] hover:text-[#d0b125] transition-colors rounded hover:bg-gray-50"
              onClick={() => setIsMenuOpen(false)}
            >
              <span className="mr-3">🎁</span>
              {t('loyaltyProgram')}
            </Link>
            <Link
              to="/mag"
              className="flex items-center py-2 px-3 text-[#2f2a1e] hover:text-[#d0b125] transition-colors rounded hover:bg-gray-50"
              onClick={() => setIsMenuOpen(false)}
            >
              <span className="mr-3">📰</span>
              {t('theMag')}
            </Link>
            <Link
              to="/our-universe"
              className="flex items-center py-2 px-3 text-[#2f2a1e] hover:text-[#d0b125] transition-colors rounded hover:bg-gray-50"
              onClick={() => setIsMenuOpen(false)}
            >
              <span className="mr-3">🌍</span>
              {t('ourUniverse')}
            </Link>
            <Link
              to="/help"
              className="flex items-center py-2 px-3 text-[#2f2a1e] hover:text-[#d0b125] transition-colors rounded hover:bg-gray-50"
              onClick={() => setIsMenuOpen(false)}
            >
              <span className="mr-3">❓</span>
              {t('help')}
            </Link>
          </div>
        </div>

        {/* Cart */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4">
          <Link
            to="/cart"
            className="flex items-center justify-between py-3 px-4 bg-[#d0b125] text-white rounded-lg hover:bg-[#b89a20] transition-colors font-medium"
            onClick={() => setIsMenuOpen(false)}
          >
            <div className="flex items-center">
              <span className="mr-2">🛒</span>
              {t('cart')}
            </div>
            <div className="bg-white text-[#d0b125] rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">
              {cartItemsCount}
            </div>
          </Link>
        </div>
      </div>
    </div>
  );

  return (
    <nav className="bg-[#fffaf3] shadow-sm sticky top-0 z-50">
      {/* Indicateur de traduction */}
      {isTranslating && (
        <div className="bg-[#d0b125] text-white text-center py-1 text-sm">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
            {t('translating')}
          </div>
        </div>
      )}

      {/* Top bar with styled logo */}
      <div className="py-3 px-4 border-b border-[#d0b125]/30">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          {/* Left section - Mobile menu button and Search */}
          <div className="flex items-center space-x-4">
            {/* Mobile menu button */}
            <button
              onClick={() => setIsMenuOpen(true)}
              className="lg:hidden flex items-center text-[#2f2a1e] hover:text-[#d0b125] transition"
            >
              <span className="text-xl mr-1">☰</span>
              <span className="text-sm font-medium">{t('menu')}</span>
            </button>

            {/* Search - Hidden on mobile in top bar (shown in mobile menu) */}
            <div className="hidden lg:flex items-center space-x-3 text-sm text-[#2f2a1e]">
              <div className="relative">
                <button
                  onClick={() => setIsSearchOpen(!isSearchOpen)}
                  className="flex items-center hover:text-[#d0b125] transition cursor-pointer"
                >
                  <span className="mr-1">🔍</span>
                  <span>{t('search')}</span>
                </button>
                
                {/* Search Modal */}
                {isSearchOpen && (
                  <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-start justify-center pt-20">
                    <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl mx-4">
                      <div className="p-4 border-b border-gray-200">
                        <div className="flex items-center">
                          <form onSubmit={handleSearchSubmit} className="flex-1">
                            <div className="relative">
                              <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder={t('search')}
                                className="w-full px-4 py-3 pl-10 pr-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#d0b125] focus:border-[#d0b125]"
                                autoFocus
                              />
                              <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                                🔍
                              </div>
                              <button
                                type="submit"
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 bg-[#d0b125] text-white px-3 py-1 rounded text-sm hover:bg-[#b89a20] transition-colors"
                              >
                                {t('search')}
                              </button>
                            </div>
                          </form>
                          <button
                            onClick={() => {
                              setIsSearchOpen(false);
                              setSearchQuery("");
                            }}
                            className="ml-4 text-gray-500 hover:text-gray-700"
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                      
                      {/* Search Results */}
                      {renderSearchResults()}
                    </div>
                  </div>
                )}
              </div>
              
              <span className="text-[#b8b2a6]">|</span>
              <Link to="/help" className="hover:text-[#d0b125] transition">
                {t('help')}
              </Link>
            </div>
          </div>

          {/* Styled logo with decorative dots */}
          <div className="flex items-center justify-center flex-1 mx-4">
            <div className="flex items-center justify-center w-full max-w-md">
              {/* Left decorative dots - Hidden on mobile */}
              <div className="hidden lg:flex items-center space-x-1 mr-4">
                <div className="w-1.5 h-1.5 bg-[#d0b125] rounded-full opacity-60"></div>
                <div className="w-1.5 h-1.5 bg-[#d0b125] rounded-full opacity-60"></div>
                <div className="w-1.5 h-1.5 bg-[#d0b125] rounded-full opacity-60"></div>
              </div>
              
              {/* Logo with effects */}
              <Link to="/" className="flex items-center justify-center group relative">
                <div className="absolute -inset-2 bg-gradient-to-r from-[#d0b125]/10 to-[#2f2a1e]/5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-sm hidden lg:block"></div>
                
                <img
                  src="/images/logi d4u dré noir.png"
                  alt="D4U Naturally"
                  className="h-16 lg:h-20 w-auto object-contain transform group-hover:scale-105 transition-all duration-300"
                  onError={(e) => {
                    e.target.src = '/images/placeholder.jpg';
                  }}
                />
              </Link>
              
              {/* Right decorative dots - Hidden on mobile */}
              <div className="hidden lg:flex items-center space-x-1 ml-4">
                <div className="w-1.5 h-1.5 bg-[#d0b125] rounded-full opacity-60"></div>
                <div className="w-1.5 h-1.5 bg-[#d0b125] rounded-full opacity-60"></div>
                <div className="w-1.5 h-1.5 bg-[#d0b125] rounded-full opacity-60"></div>
              </div>
            </div>
          </div>

          {/* Right section */}
          <div className="flex items-center space-x-3 text-sm text-[#2f2a1e]">
            {/* Language Selector - Hidden on mobile (shown in mobile menu) */}
            <div className="hidden lg:block relative">
              <button
                onClick={() => setIsLanguageOpen(!isLanguageOpen)}
                className="flex items-center space-x-1 hover:text-[#d0b125] transition cursor-pointer"
                disabled={isTranslating}
              >
                <span>{getCurrentLanguage().flag}</span>
                <span className="uppercase">{getCurrentLanguage().code}</span>
                <span className="text-xs">▼</span>
                {isTranslating && (
                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-[#d0b125] ml-1"></div>
                )}
              </button>
              
              {isLanguageOpen && (
                <div className="absolute top-full right-0 mt-2 bg-white shadow-lg rounded-lg py-2 w-32 z-50 border border-[#d0b125]/20">
                  {languageOptions.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => handleLanguageChange(lang.code)}
                      disabled={isTranslating}
                      className={`flex items-center space-x-2 w-full px-3 py-2 text-left hover:bg-[#fffaf3] transition-colors ${
                        language === lang.code ? "text-[#d0b125] bg-[#fffaf3]" : "text-[#2f2a1e]"
                      } ${isTranslating ? "opacity-50 cursor-not-allowed" : ""}`}
                    >
                      <span>{lang.flag}</span>
                      <span className="flex-1">{lang.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <span className="hidden lg:inline text-[#b8b2a6]">|</span>
            
            {/* Account - Hidden on mobile (shown in mobile menu) */}
            <Link 
              to={user ? "/my-account" : "/login"} 
              className="hidden lg:block hover:text-[#d0b125] transition"
            >
              {user ? t('myAccount') : t('login')}
            </Link>

            {user && (
              <>
                <span className="hidden lg:inline text-[#b8b2a6]">|</span>
                <button
                  onClick={handleLogout}
                  className="hidden lg:block hover:text-[#d0b125] transition font-serif font-light"
                >
                  {t('logout')}
                </button>
              </>
            )}

            <span className="hidden lg:inline text-[#b8b2a6]">|</span>
            
            {/* Cart - Always visible */}
            <Link to="/cart" className="flex items-center hover:text-[#d0b125] transition relative">
              <span className="mr-1">🛒</span>
              <span className="hidden lg:inline">{t('cart')}</span>
              {cartItemsCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-[#d0b125] text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold">
                  {cartItemsCount}
                </span>
              )}
            </Link>
          </div>
        </div>
      </div>

      {/* Main navigation organized by gender - Hidden on mobile */}
      <div className="hidden lg:block max-w-7xl mx-auto px-4">
        <div className="flex justify-center space-x-12 py-4">
          {/* All Products */}
          <div className="relative group flex flex-col items-center"
               onMouseEnter={() => setHoveredCategory('all')}
               onMouseLeave={() => setHoveredCategory(null)}>
            <Link to="/shop" className="block">
              <div className={`font-serif text-[15px] tracking-wider transition-all duration-300 ${
                location.pathname === '/shop' ? 'text-[#d0b125]' : 'text-[#2f2a1e]'
              }`}>
                <span className="flex items-center cursor-pointer hover:text-[#d0b125] font-light">
                  {t('allProducts')}
                </span>
              </div>
            </Link>
            <div className={`h-0.5 w-8 mt-1 transition-all duration-300 ${
              location.pathname === '/shop' ? 'bg-[#d0b125]' : 'bg-transparent'
            } group-hover:bg-[#d0b125]`}></div>

            {/* All Products Dropdown */}
            {hoveredCategory === 'all' && (
              <div className="absolute left-1/2 transform -translate-x-1/2 mt-3 bg-white shadow-xl rounded-lg p-6 w-96 z-50 border border-[#d0b125]/20">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-serif text-lg text-[#2f2a1e] font-light">
                    {t('allProductsTitle')}
                  </h3>
                  <Link
                    to="/shop"
                    className="text-sm text-[#d0b125] hover:text-[#2f2a1e] transition-colors font-light"
                    onClick={() => setHoveredCategory(null)}
                  >
                    {t('viewAll')}
                  </Link>
                </div>
                <div className="grid gap-3">
                  {translatedCategories.map((cat) => (
                    <div 
                      key={cat.id}
                      className="relative"
                      onMouseEnter={() => setHoveredSubCategory(cat.id)}
                      onMouseLeave={() => setHoveredSubCategory(null)}
                    >
                      <Link
                        to={`/shop/${cat.id}`}
                        className="flex items-center p-3 hover:bg-[#fffaf3] rounded-lg transition-all duration-300 group/item"
                        onClick={() => setHoveredCategory(null)}
                      >
                        <div className="w-10 h-10 bg-[#d0b125]/10 rounded-full flex items-center justify-center mr-3">
                          <span className="text-[#d0b125] text-sm font-light">
                            {cat.gender === 'women' ? 'W' : cat.gender === 'men' ? 'M' : 'U'}
                          </span>
                        </div>
                        <div>
                          <span className="font-medium text-[#2f2a1e] group-hover/item:text-[#d0b125] transition-colors">
                            {cat.name}
                          </span>
                          <p className="text-xs text-gray-500 mt-1 font-light">
                            {(cat.products?.length || 0)} {t('products')}
                          </p>
                        </div>
                      </Link>
                      
                      {hoveredSubCategory === cat.id && cat.products && cat.products.length > 0 && (
                        renderProductsPreview(cat)
                      )}
                    </div>
                  ))}
                </div>
                
                {allProducts.length > 0 && (
                  <div className="mt-6 pt-6 border-t border-[#d0b125]/20">
                    {renderProductsPreview({}, true)}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Women Categories */}
          {womenCategories.length > 0 && (
            <div className="relative group flex flex-col items-center"
                 onMouseEnter={() => setHoveredCategory('women')}
                 onMouseLeave={() => setHoveredCategory(null)}>
              <Link to="/shop?gender=women" className="block">
                <div className={`font-serif text-[15px] tracking-wider transition-all duration-300 ${
                  location.pathname.includes('/women') || location.search.includes('gender=women') ? 'text-[#d0b125]' : 'text-[#2f2a1e]'
                }`}>
                  <span className="flex items-center cursor-pointer hover:text-[#d0b125] font-light">
                    {t('womensCare')}
                  </span>
                </div>
              </Link>
              <div className={`h-0.5 w-8 mt-1 transition-all duration-300 ${
                location.pathname.includes('/women') || location.search.includes('gender=women') ? 'bg-[#d0b125]' : 'bg-transparent'
              } group-hover:bg-[#d0b125]`}></div>

              {hoveredCategory === 'women' && (
                <div className="absolute left-1/2 transform -translate-x-1/2 mt-3 bg-white shadow-xl rounded-lg p-6 w-96 z-50 border border-[#d0b125]/20">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="font-serif text-lg text-[#2f2a1e] font-light">
                      {t('womensCollection')}
                    </h3>
                    <Link
                      to="/shop?gender=women"
                      className="text-sm text-[#d0b125] hover:text-[#2f2a1e] transition-colors font-light"
                      onClick={() => setHoveredCategory(null)}
                    >
                      {t('allWomenProducts')}
                    </Link>
                  </div>
                  <div className="grid gap-3">
                    {womenCategories.map((cat) => (
                      <div 
                        key={cat.id}
                        className="relative"
                        onMouseEnter={() => setHoveredSubCategory(cat.id)}
                        onMouseLeave={() => setHoveredSubCategory(null)}
                      >
                        <Link
                          to={`/shop/${cat.id}`}
                          className="flex items-center p-3 hover:bg-[#fffaf3] rounded-lg transition-all duration-300 group/item"
                          onClick={() => setHoveredCategory(null)}
                        >
                          <div className="w-10 h-10 bg-[#d0b125]/10 rounded-full flex items-center justify-center mr-3">
                            <span className="text-[#d0b125] text-sm font-light">W</span>
                          </div>
                          <div>
                            <span className="font-medium text-[#2f2a1e] group-hover/item:text-[#d0b125] transition-colors">
                              {cat.name}
                            </span>
                            <p className="text-xs text-gray-500 mt-1 font-light">
                              {(cat.products?.length || 0)} {t('products')}
                            </p>
                          </div>
                        </Link>
                        
                        {hoveredSubCategory === cat.id && cat.products && cat.products.length > 0 && (
                          renderProductsPreview(cat)
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Men Categories */}
          {menCategories.length > 0 && (
            <div className="relative group flex flex-col items-center"
                 onMouseEnter={() => setHoveredCategory('men')}
                 onMouseLeave={() => setHoveredCategory(null)}>
              <Link to="/shop?gender=men" className="block">
                <div className={`font-serif text-[15px] tracking-wider transition-all duration-300 ${
                  location.pathname.includes('/men') || location.search.includes('gender=men') ? 'text-[#d0b125]' : 'text-[#2f2a1e]'
                }`}>
                  <span className="flex items-center cursor-pointer hover:text-[#d0b125] font-light">
                    {t('mensCare')}
                  </span>
                </div>
              </Link>
              <div className={`h-0.5 w-8 mt-1 transition-all duration-300 ${
                location.pathname.includes('/men') || location.search.includes('gender=men') ? 'bg-[#d0b125]' : 'bg-transparent'
              } group-hover:bg-[#d0b125]`}></div>

              {hoveredCategory === 'men' && (
                <div className="absolute left-1/2 transform -translate-x-1/2 mt-3 bg-white shadow-xl rounded-lg p-6 w-96 z-50 border border-[#d0b125]/20">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="font-serif text-lg text-[#2f2a1e] font-light">
                      {t('mensCollection')}
                    </h3>
                    <Link
                      to="/shop?gender=men"
                      className="text-sm text-[#d0b125] hover:text-[#2f2a1e] transition-colors font-light"
                      onClick={() => setHoveredCategory(null)}
                    >
                      {t('allMenProducts')}
                    </Link>
                  </div>
                  <div className="grid gap-3">
                    {menCategories.map((cat) => (
                      <div 
                        key={cat.id}
                        className="relative"
                        onMouseEnter={() => setHoveredSubCategory(cat.id)}
                        onMouseLeave={() => setHoveredSubCategory(null)}
                      >
                        <Link
                          to={`/shop/${cat.id}`}
                          className="flex items-center p-3 hover:bg-[#fffaf3] rounded-lg transition-all duration-300 group/item"
                          onClick={() => setHoveredCategory(null)}
                        >
                          <div className="w-10 h-10 bg-[#2f2a1e]/10 rounded-full flex items-center justify-center mr-3">
                            <span className="text-[#2f2a1e] text-sm font-light">M</span>
                          </div>
                          <div>
                            <span className="font-medium text-[#2f2a1e] group-hover/item:text-[#d0b125] transition-colors">
                              {cat.name}
                            </span>
                            <p className="text-xs text-gray-500 mt-1 font-light">
                              {(cat.products?.length || 0)} {t('products')}
                            </p>
                          </div>
                        </Link>
                        
                        {hoveredSubCategory === cat.id && cat.products && cat.products.length > 0 && (
                          renderProductsPreview(cat)
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Unisex Categories */}
          {unisexCategories.length > 0 && (
            <div className="relative group flex flex-col items-center"
                 onMouseEnter={() => setHoveredCategory('unisex')}
                 onMouseLeave={() => setHoveredCategory(null)}>
              <Link to="/shop?gender=unisex" className="block">
                <div className={`font-serif text-[15px] tracking-wider transition-all duration-300 ${
                  location.pathname.includes('/unisex') || location.search.includes('gender=unisex') ? 'text-[#d0b125]' : 'text-[#2f2a1e]'
                }`}>
                  <span className="flex items-center cursor-pointer hover:text-[#d0b125] font-light">
                    {t('unisexCare')}
                  </span>
                </div>
              </Link>
              <div className={`h-0.5 w-8 mt-1 transition-all duration-300 ${
                location.pathname.includes('/unisex') || location.search.includes('gender=unisex') ? 'bg-[#d0b125]' : 'bg-transparent'
              } group-hover:bg-[#d0b125]`}></div>

              {hoveredCategory === 'unisex' && (
                <div className="absolute left-1/2 transform -translate-x-1/2 mt-3 bg-white shadow-xl rounded-lg p-6 w-96 z-50 border border-[#d0b125]/20">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="font-serif text-lg text-[#2f2a1e] font-light">
                      {t('unisexCollection')}
                    </h3>
                    <Link
                      to="/shop?gender=unisex"
                      className="text-sm text-[#d0b125] hover:text-[#2f2a1e] transition-colors font-light"
                      onClick={() => setHoveredCategory(null)}
                    >
                      {t('allUnisexProducts')}
                    </Link>
                  </div>
                  <div className="grid gap-3">
                    {unisexCategories.map((cat) => (
                      <div 
                        key={cat.id}
                        className="relative"
                        onMouseEnter={() => setHoveredSubCategory(cat.id)}
                        onMouseLeave={() => setHoveredSubCategory(null)}
                      >
                        <Link
                          to={`/shop/${cat.id}`}
                          className="flex items-center p-3 hover:bg-[#fffaf3] rounded-lg transition-all duration-300 group/item"
                          onClick={() => setHoveredCategory(null)}
                        >
                          <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center mr-3">
                            <span className="text-gray-600 text-sm font-light">U</span>
                          </div>
                          <div>
                            <span className="font-medium text-[#2f2a1e] group-hover/item:text-[#d0b125] transition-colors">
                              {cat.name}
                            </span>
                            <p className="text-xs text-gray-500 mt-1 font-light">
                              {(cat.products?.length || 0)} {t('products')}
                            </p>
                          </div>
                        </Link>
                        
                        {hoveredSubCategory === cat.id && cat.products && cat.products.length > 0 && (
                          renderProductsPreview(cat)
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Bottom compact bar */}
        <div className="flex justify-center space-x-6 py-3 border-t border-[#d0b125]/10 text-sm">
          <Link to="/loyalty-program" className="hover:text-[#d0b125] transition font-serif font-light">
            {t('loyaltyProgram')}
          </Link>
          <span className="text-[#b8b2a6]">|</span>
          <Link to="/mag" className="hover:text-[#d0b125] transition font-serif font-light">
            {t('theMag')}
          </Link>
          <span className="text-[#b8b2a6]">|</span>
          <Link to="/our-universe" className="hover:text-[#d0b125] transition font-serif font-light">
            {t('ourUniverse')}
          </Link>
        </div>
      </div>

      {/* Mobile menu overlay */}
      {isMenuOpen && renderMobileMenu()}
    </nav>
  );
}